import React from 'react';
import { db } from './database';

interface StorageAlert {
  level: 'info' | 'warning' | 'critical';
  message: string;
  percentage: number;
  action?: () => void;
}

class StorageMonitorService {
  private monitorInterval: number | null = null;
  private alertThresholds = {
    warning: 70,    // 70% full
    critical: 90,   // 90% full
  };
  
  private listeners: ((alert: StorageAlert) => void)[] = [];
  
  constructor() {
    // Check storage on initialization
    this.checkStorage();
  }
  
  startMonitoring(intervalMs: number = 30000): void {
    if (this.monitorInterval) {
      return; // Already monitoring
    }
    
    this.monitorInterval = window.setInterval(() => {
      this.checkStorage();
    }, intervalMs);
  }
  
  stopMonitoring(): void {
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
      this.monitorInterval = null;
    }
  }
  
  onAlert(callback: (alert: StorageAlert) => void): () => void {
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }
  
  private notifyListeners(alert: StorageAlert): void {
    this.listeners.forEach(listener => listener(alert));
  }
  
  async checkStorage(): Promise<StorageAlert | null> {
    try {
      const estimate = await db.getStorageEstimate();
      
      if (estimate.percentage >= this.alertThresholds.critical) {
        const alert: StorageAlert = {
          level: 'critical',
          message: `Storage critically low: ${estimate.percentage.toFixed(1)}% used`,
          percentage: estimate.percentage,
          action: () => this.clearCache(),
        };
        this.notifyListeners(alert);
        return alert;
      }
      
      if (estimate.percentage >= this.alertThresholds.warning) {
        const alert: StorageAlert = {
          level: 'warning',
          message: `Storage usage high: ${estimate.percentage.toFixed(1)}% used`,
          percentage: estimate.percentage,
          action: () => this.showStorageManagement(),
        };
        this.notifyListeners(alert);
        return alert;
      }
      
      // Check quota against license
      const license = await db.getCurrentLicense();
      const usageInfo = await this.getDetailedUsage();
      
      if (license && usageInfo.totalSizeGB > license.quotas.storageGB * 0.9) {
        const alert: StorageAlert = {
          level: 'warning',
          message: `Approaching storage quota: ${(usageInfo.totalSizeGB / license.quotas.storageGB * 100).toFixed(1)}% of quota used`,
          percentage: (usageInfo.totalSizeGB / license.quotas.storageGB * 100),
          action: () => this.showUpgradeOptions(),
        };
        this.notifyListeners(alert);
        return alert;
      }
      
      return null;
    } catch (error) {
      console.error('Storage check failed:', error);
      return null;
    }
  }
  
  async getDetailedUsage(): Promise<{
    documents: { count: number; sizeBytes: number };
    models: { count: number; sizeBytes: number };
    cache: { sizeBytes: number };
    totalSizeGB: number;
    breakdown: Array<{ type: string; sizeBytes: number; percentage: number }>;
  }> {
    // Get document storage
    const documents = await db.getAllDocuments();
    const documentSize = documents.reduce((sum, doc) => sum + doc.size, 0);
    
    // Estimate model cache size
    let modelSize = 0;
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const name of cacheNames) {
        if (name.includes('model')) {
          const cache = await caches.open(name);
          const keys = await cache.keys();
          
          for (const request of keys) {
            const response = await cache.match(request);
            if (response) {
              const blob = await response.blob();
              modelSize += blob.size;
            }
          }
        }
      }
    }
    
    // Get total storage estimate
    const estimate = await db.getStorageEstimate();
    const totalBytes = estimate.usage || 0;
    const otherSize = Math.max(0, totalBytes - documentSize - modelSize);
    
    const breakdown = [
      { type: 'Documents', sizeBytes: documentSize, percentage: (documentSize / totalBytes) * 100 },
      { type: 'AI Models', sizeBytes: modelSize, percentage: (modelSize / totalBytes) * 100 },
      { type: 'App Cache', sizeBytes: otherSize, percentage: (otherSize / totalBytes) * 100 },
    ];
    
    return {
      documents: { count: documents.length, sizeBytes: documentSize },
      models: { count: modelSize > 0 ? 1 : 0, sizeBytes: modelSize },
      cache: { sizeBytes: otherSize },
      totalSizeGB: totalBytes / (1024 * 1024 * 1024),
      breakdown,
    };
  }
  
  async clearCache(aggressive: boolean = false): Promise<{ clearedBytes: number }> {
    let clearedBytes = 0;
    
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      
      for (const name of cacheNames) {
        // Skip model cache unless aggressive
        if (!aggressive && name.includes('model')) {
          continue;
        }
        
        // Skip critical app shell cache
        if (name === 'exan-v1' && !aggressive) {
          continue;
        }
        
        try {
          const cache = await caches.open(name);
          const keys = await cache.keys();
          
          // Estimate size before deletion
          for (const request of keys) {
            const response = await cache.match(request);
            if (response) {
              const blob = await response.blob();
              clearedBytes += blob.size;
            }
          }
          
          // Delete cache
          await caches.delete(name);
        } catch (error) {
          console.error(`Failed to clear cache ${name}:`, error);
        }
      }
    }
    
    // Clean up old analytics
    await db.cleanup();
    
    // Notify service worker to clear its caches
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_CACHE',
        cacheName: aggressive ? null : 'data',
      });
    }
    
    return { clearedBytes };
  }
  
  async requestPersistentStorage(): Promise<boolean> {
    try {
      const isPersisted = await db.requestPersistentStorage();
      
      if (isPersisted) {
        const alert: StorageAlert = {
          level: 'info',
          message: 'Storage is now persistent. Your data is protected from automatic cleanup.',
          percentage: 0,
        };
        this.notifyListeners(alert);
      }
      
      return isPersisted;
    } catch (error) {
      console.error('Failed to request persistent storage:', error);
      return false;
    }
  }
  
  private showStorageManagement(): void {
    // This would trigger a UI component to show storage management
    window.dispatchEvent(new CustomEvent('show-storage-management'));
  }
  
  private showUpgradeOptions(): void {
    // This would trigger a UI component to show upgrade options
    window.dispatchEvent(new CustomEvent('show-upgrade-options'));
  }
  
  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

// Export singleton instance
export const storageMonitor = new StorageMonitorService();

// React hook for storage monitoring
export function useStorageMonitor() {
  const [usage, setUsage] = React.useState<Awaited<ReturnType<typeof storageMonitor.getDetailedUsage>>>();
  const [alert, setAlert] = React.useState<StorageAlert | null>(null);
  
  React.useEffect(() => {
    // Get initial usage
    storageMonitor.getDetailedUsage().then(setUsage);
    
    // Subscribe to alerts
    const unsubscribe = storageMonitor.onAlert(setAlert);
    
    // Start monitoring
    storageMonitor.startMonitoring();
    
    // Update usage periodically
    const interval = setInterval(() => {
      storageMonitor.getDetailedUsage().then(setUsage);
    }, 10000);
    
    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, []);
  
  return { usage, alert, formatBytes: storageMonitor.formatBytes };
}